import { useState, useEffect } from 'react';
import { Box, Chip, FormControlLabel, Checkbox } from '@mui/material';

// Each checkbox animates in/out on its own, this far apart, so they appear one by one.
const STAGGER_MS = 35;
const DURATION_MS = 70;

export default function TOBModuleOptions({ modules, selectedModules, onToggleModule }) {
    // The chip toggles the checkboxes open and closed.
    const [open, setOpen] = useState(false);

    // mounted: the checkboxes are rendered; shown: they are animated in.
    // They stay mounted while animating out, then unmount so they stop taking up space.
    const [mounted, setMounted] = useState(open);
    const [shown, setShown] = useState(open);

    useEffect(() => {
        if (open) {
            setMounted(true);
            // Wait two frames so they're painted hidden first, otherwise there's nothing to transition from
            let frame = requestAnimationFrame(() => {
                frame = requestAnimationFrame(() => setShown(true));
            });
            return () => cancelAnimationFrame(frame);
        }
        setShown(false);
        const timer = setTimeout(() => setMounted(false), DURATION_MS + STAGGER_MS * modules.length);
        return () => clearTimeout(timer);
    }, [open, modules.length]);

    return (
        <Box
            sx={{
                display: 'inline-flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                columnGap: 1.5,
                maxWidth: '100%',
                mb: 1,
            }}
        >
            <Chip
                label="Show / Hide Modules"
                size="small"
                onClick={() => setOpen(!open)}
                aria-pressed={open}
                sx={{
                    fontSize: 12,
                    cursor: 'pointer',
                    mr: 0.5,
                    color: '#757575',
                    backgroundColor: '#eeeeee',
                    '&:hover': { backgroundColor: '#eeeeee' },
                }}
            />
            {mounted && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.5 }}>
                    {modules.map((module, i) => (
                        <FormControlLabel
                            key={module.name}
                            sx={{
                                m: 0,
                                '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' },
                                opacity: shown ? 1 : 0,
                                transform: shown ? 'none' : 'translateX(-8px) scale(0.95)',
                                transition: `opacity ${DURATION_MS}ms ease-out, transform ${DURATION_MS}ms ease-out`,
                                // In left to right, out right to left
                                transitionDelay: `${(shown ? i : modules.length - 1 - i) * STAGGER_MS}ms`,
                                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                            }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={selectedModules.includes(module.name)}
                                    onChange={() => onToggleModule(module.name)}
                                    sx={{ p: 0.25, mr: 0.25, color: '#757575', '&.Mui-checked': { color: '#757575' }, '& .MuiSvgIcon-root': { fontSize: 16 } }}
                                />
                            }
                            label={module.name}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}
